/**
 * TrustedBySection — "Trusted by research teams at" social-proof band.
 *
 * Structure mirrors the Figma reference band (node 1:21, "Trusted by engineering
 * teams at"): a centered heading above a row of institution marks. Brand override:
 * this band is the page's ONE dark surface — a Stanford Teal (#00505C =
 * `bg-secondary`) field, so it reads as a hard banded stripe across the light page.
 *
 * The three institution marks are the REAL wordmark logos (vectorized SVGs)
 * dropped straight onto the Teal field (no chip/box), in a single static, centered
 * row — no marquee, no animation. The row wraps on very narrow screens so the marks
 * never overflow.
 *
 * COLOR: the source wordmarks are mixed-color art (UCLA is white; Stanford and
 * UC Irvine are black). To render all three uniformly WHITE on the Teal field, each
 * <img> carries `brightness-0 invert` — brightness-0 flattens any art to a black
 * silhouette (preserving alpha), then invert flips it to pure white. Pure-white
 * wordmarks are already high-contrast on Teal, so there is NO drop-shadow glow.
 *
 * SIZING: these are text wordmarks with differing aspect ratios (Stanford ≈4.7:1,
 * UCLA ≈3.1:1, UC Irvine ≈5.9:1), so equal heights would NOT read as equal. The
 * per-logo (responsive) heights optically balance the three marks against each
 * other so the row reads as similarly-sized wordmarks.
 *
 * ZERO MUI.
 */
import * as React from 'react';

/* ────────────────────────────────────────────────────────────────────────────
 * Institutions — the real studies, rendered as their real wordmark logos.
 * `imgClass` carries a per-logo (responsive) height that optically balances the
 * three marks against each other; their aspect ratios differ, so equal heights
 * would not look equal.
 * ──────────────────────────────────────────────────────────────────────────── */
type Institution = {
  readonly name: string;
  readonly src: string;
  readonly alt: string;
  readonly imgClass: string;
};

const INSTITUTIONS: readonly Institution[] = [
  {
    name: 'Stanford',
    src: '/stanford.svg',
    alt: 'Stanford University',
    // Wordmark ≈4.7:1.
    imgClass: 'h-10 md:h-12',
  },
  {
    name: 'UCLA',
    src: '/ucla.svg',
    alt: 'UCLA',
    // Wordmark ≈3.1:1 → shorter at a given width, so a touch taller to balance.
    imgClass: 'h-11 md:h-14',
  },
  {
    name: 'UC Irvine',
    src: '/ucirvine.svg',
    alt: 'University of California, Irvine',
    // Wordmark ≈5.9:1 → widest, so a touch shorter to balance.
    imgClass: 'h-9 md:h-11',
  },
];

const TrustedBySection: React.FC = () => (
  <section
    aria-label="Trusted by research teams"
    // The logo ROW is the band's only in-flow child; the vertical padding positions it.
    // Padding is intentionally TOP-heavier than the bottom (pt-20/pb-12, md:pt-24/pb-16)
    // so the row sits a touch BELOW the band's vertical center — by (paddingTop −
    // paddingBottom)/2 ≈ 16px. The heading is pulled OUT of flow (absolute, below) and
    // floats in the upper zone so it sits ABOVE the logos. Top+bottom totals match the
    // prior symmetric py, so the band height (and the viewport-fit layout) is unchanged.
    className="relative flex flex-col justify-center bg-secondary pt-20 pb-12 md:pt-24 md:pb-16"
  >
    {/* Heading — pinned to the upper zone (out of flow) so it sits ABOVE the centered
        logo row. inset-x-0 + text-center keeps it horizontally centered. */}
    <h2 className="absolute inset-x-0 top-8 m-0 px-4 text-center font-sans text-sm font-bold uppercase tracking-[0.2em] text-secondary-foreground/90 md:top-10 md:text-base">
      Trusted by research teams at
    </h2>

    {/* Vertically-centered row of the 3 wordmarks; wraps on very narrow screens.
        `brightness-0 invert` renders each mark pure white on the Teal field. */}
    <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-10 px-4 md:gap-x-24">
      {INSTITUTIONS.map((institution) => (
        <img
          key={institution.name}
          src={institution.src}
          alt={institution.alt}
          className={`w-auto object-contain brightness-0 invert ${institution.imgClass}`}
          // Load eagerly (NOT lazy): this band sits just below a tall hero, so on most
          // desktop window heights the logos land at/below the fold at first paint. With
          // lazy loading the browser would defer them until a scroll brought them into
          // view, leaving the band blank until you scrolled. They're tiny SVGs, so eager
          // loading them costs nothing and guarantees they're always visible immediately.
          loading="eager"
          decoding="async"
          draggable={false}
        />
      ))}
    </div>
  </section>
);

export default TrustedBySection;
